import { useEffect, useMemo, useState } from "react";
import { fetchStorefrontCategories } from "../api/catalog";
import CatalogFilters from "../components/CatalogFilters";
import InfiniteScrollSentinel from "../components/InfiniteScrollSentinel";
import ProductCard from "../components/ProductCard";
import StoreBoot from "../components/StoreBoot";
import StoreCredit from "../components/StoreCredit";
import StoreHeader from "../components/StoreHeader";
import StorePixels from "../components/StorePixels";
import { useInfiniteCatalog } from "../hooks/useInfiniteCatalog";

function toAmountMinor(value) {
  if (value === "") return "";
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : "";
}

export default function StorefrontPage({ storeSlug }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [appliedPriceMin, setAppliedPriceMin] = useState("");
  const [appliedPriceMax, setAppliedPriceMax] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const priceRangeInvalid =
    priceMin !== "" &&
    priceMax !== "" &&
    Number(priceMin) > Number(priceMax);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (priceRangeInvalid) {
        setAppliedPriceMin("");
        setAppliedPriceMax("");
        return;
      }
      setAppliedPriceMin(toAmountMinor(priceMin));
      setAppliedPriceMax(toAmountMinor(priceMax));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [priceMax, priceMin, priceRangeInvalid]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingCategories(true);
    fetchStorefrontCategories(storeSlug, { signal: controller.signal })
      .then((result) => setCategories(result?.categories || []))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setCategories([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCategories(false);
      });
    return () => controller.abort();
  }, [storeSlug]);

  const filters = useMemo(
    () => ({
      search,
      category,
      minPrice: appliedPriceMin,
      maxPrice: appliedPriceMax,
    }),
    [appliedPriceMax, appliedPriceMin, category, search]
  );
  const {
    store,
    products,
    pagination,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    retry,
  } = useInfiniteCatalog(storeSlug, filters);

  const showBlockingError = error && products.length === 0 && !loading;
  const hasActiveFilters = Boolean(searchInput || category || priceMin || priceMax);

  function clearFilters() {
    setSearchInput("");
    setSearch("");
    setCategory("");
    setPriceMin("");
    setPriceMax("");
    setAppliedPriceMin("");
    setAppliedPriceMax("");
  }

  if (loading && products.length === 0 && !error) return <StoreBoot />;

  return (
    <div className="storefront">
      <StorePixels store={store} />
      <StoreHeader store={store} storeSlug={storeSlug} />

      <main className="catalog">
        <div className="catalog__heading">
          <div>
            <h2>Todos os produtos</h2>
          </div>
          {pagination && (
            <p className="catalog__count">
              {pagination.total}{" "}
              {pagination.total === 1 ? "produto" : "produtos"}
            </p>
          )}
        </div>

        <label className="catalog-search">
          <span className="sr-only">Buscar produtos por nome</span>
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
          </svg>
          <input
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Buscar produtos por nome..."
            type="search"
            value={searchInput}
          />
        </label>

        <div className="catalog-layout">
          <CatalogFilters
            categories={categories}
            category={category}
            hasActiveFilters={hasActiveFilters}
            loadingCategories={loadingCategories}
            onCategoryChange={setCategory}
            onClear={clearFilters}
            onPriceMaxChange={setPriceMax}
            onPriceMinChange={setPriceMin}
            priceMax={priceMax}
            priceMin={priceMin}
            priceRangeInvalid={priceRangeInvalid}
          />

          <div className="catalog-results">
            {showBlockingError ? (
              <section className="state-card" role="alert">
                <h2>
                  {error.status === 404
                    ? "Loja não encontrada"
                    : "Não foi possível abrir a loja"}
                </h2>
                <p>
                  {error.status === 404
                    ? "Confira o endereço ou aguarde até que a loja seja publicada."
                    : error.message}
                </p>
                <button type="button" onClick={retry}>
                  Tentar novamente
                </button>
              </section>
            ) : products.length === 0 ? (
              <section className="state-card">
                <h2>Nenhum produto encontrado</h2>
                <p>
                  {hasActiveFilters
                    ? "Tente ajustar ou limpar os filtros do catálogo."
                    : "Esta loja ainda não publicou produtos para venda."}
                </p>
                {hasActiveFilters && (
                  <button type="button" onClick={clearFilters}>
                    Limpar filtros
                  </button>
                )}
              </section>
            ) : (
              <>
                <div className="product-grid">
                  {products.map((product) => (
                    <ProductCard
                      product={product}
                      storeSlug={storeSlug}
                      key={product.id}
                    />
                  ))}
                </div>

                {error && (
                  <div className="inline-error" role="alert">
                    <span>Não foi possível carregar mais produtos.</span>
                    <button type="button" onClick={retry}>
                      Tentar novamente
                    </button>
                  </div>
                )}

                {loadingMore && (
                  <div className="loading-more" role="status">
                    <span className="spinner" />
                    Carregando mais produtos...
                  </div>
                )}

                <InfiniteScrollSentinel
                  enabled={hasMore}
                  loading={loadingMore}
                  onVisible={loadMore}
                />

                {!hasMore && products.length > 0 && (
                  <p className="catalog__end">Você viu todos os produtos.</p>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="store-footer">
        <StoreCredit />
      </footer>
    </div>
  );
}
